import { useId, useRef, useState, type KeyboardEvent } from "react";
import { postsForRoute } from "@/blog/data";
import { SHOW_SISTEMA_LUCENTIA } from "@/config/features";
import { useReadArticles } from "@/blog/readHistory";
import { BLOG_ROUTES, blogRoutePath } from "@/blog/routes";
import { TextLink } from "@/components/ui/Button";
import { RouteDial, ROUTE_TAG_POSITIONS } from "./RouteDial";
import { RouteSteps } from "./RouteSteps";

/**
 * “Elegí tu ruta”: one tab per piece of the Sistema Lucentia®, placed around
 * the sunburst on large screens, each showing its articles in reading order.
 */
export function RouteExplorer() {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const routes = BLOG_ROUTES.map((route) => ({ ...route, posts: postsForRoute(route.id) }));
  const firstWithPosts = Math.max(0, routes.findIndex((route) => route.posts.length > 0));
  const [active, setActive] = useState(firstWithPosts);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const read = useReadArticles();
  const current = routes[active];
  const minutes = current.posts.reduce((sum, post) => sum + post.readingMinutes, 0);

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const keys: Record<string, number> = {
      ArrowRight: active + 1,
      ArrowDown: active + 1,
      ArrowLeft: active - 1,
      ArrowUp: active - 1,
      Home: 0,
      End: routes.length - 1,
    };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const next = (keys[event.key] + routes.length) % routes.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  }

  return (
    <section aria-labelledby={`routes-${uid}`} className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="light-pool-navy relative overflow-hidden rounded-[2.25rem] bg-brand-primary px-6 py-10 text-white [--pool-x:18%] [--pool-y:30%] sm:px-10 sm:py-12 lg:grid lg:grid-cols-[minmax(0,24rem)_1fr] lg:gap-14 lg:px-12 lg:py-14">
        <div>
          <h2
            id={`routes-${uid}`}
            className="font-display text-3xl font-semibold leading-[1.1] tracking-[-0.02em]"
          >
            Elegí tu ruta
          </h2>
          {SHOW_SISTEMA_LUCENTIA ? (
            <p className="mt-3 max-w-md leading-relaxed text-on-navy-muted">
              Cinco rutas, una por cada pieza del Sistema Lucentia®. Cada una empieza por lo básico y avanza paso a paso.
            </p>
          ) : null}

          <div className="relative mt-8 lg:mx-auto lg:aspect-square lg:w-full lg:max-w-[22rem]">
            <div className="hidden lg:block">
              <RouteDial active={active} centerLabel="Tu negocio" />
            </div>
            <div
              role="tablist"
              aria-label="Rutas del blog"
              aria-orientation="horizontal"
              className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] lg:static lg:m-0 lg:block lg:overflow-visible lg:p-0"
            >
              {routes.map((route, i) => {
                const selected = i === active;
                return (
                  <button
                    key={route.id}
                    ref={(el) => {
                      tabRefs.current[i] = el;
                    }}
                    type="button"
                    role="tab"
                    id={`tab-${uid}-${route.id}`}
                    aria-selected={selected}
                    aria-controls={`panel-${uid}`}
                    tabIndex={selected ? 0 : -1}
                    onClick={() => setActive(i)}
                    onKeyDown={onKeyDown}
                    style={{
                      ["--x" as string]: `${ROUTE_TAG_POSITIONS[i].left}%`,
                      ["--y" as string]: `${ROUTE_TAG_POSITIONS[i].top}%`,
                    }}
                    className={`shrink-0 whitespace-nowrap rounded-full border px-3.5 py-2 text-sm font-semibold transition-[background-color,border-color,color,box-shadow,scale] duration-300 lg:absolute lg:left-[var(--x)] lg:top-[var(--y)] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:px-3 lg:py-1.5 lg:text-xs ${
                      selected
                        ? "border-brand-secondary bg-brand-secondary text-brand-primary shadow-[0_10px_28px_-10px_rgb(114_181_177/0.9)]"
                        : "border-white/15 bg-navy-deep text-on-navy-muted hover:border-brand-secondary/70 hover:text-white motion-safe:hover:scale-105"
                    }`}
                  >
                    {route.label}
                    <span className="sr-only">,</span> · {route.posts.length}
                    <span className="sr-only"> {route.posts.length === 1 ? "artículo" : "artículos"}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div
          role="tabpanel"
          id={`panel-${uid}`}
          aria-labelledby={`tab-${uid}-${current.id}`}
          className="mt-10 lg:mt-0"
        >
          <div className="flex flex-col gap-2 border-b border-white/12 pb-5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6">
            <h3 className="font-display text-2xl font-semibold leading-tight">{current.title}</h3>
            {current.posts.length ? (
              <p className="shrink-0 text-sm font-semibold text-teal-light">
                {current.posts.length} {current.posts.length === 1 ? "artículo" : "artículos"} · {minutes} min
              </p>
            ) : null}
          </div>
          {current.posts.length ? (
            <>
              <div className="mt-3">
                <RouteSteps posts={current.posts} read={read} tone="navy" headingLevel="h4" />
              </div>
              <div className="mt-6">
                <TextLink to={blogRoutePath(current.id)} tone="light">
                  Ver la ruta completa
                </TextLink>
              </div>
            </>
          ) : (
            <div className="mt-6 max-w-lg">
              <p className="leading-relaxed text-on-navy-muted">
                {current.description} Los primeros artículos de esta ruta están en preparación.
              </p>
              {SHOW_SISTEMA_LUCENTIA || current.service.to !== "/metodo" ? (
                <div className="mt-5">
                  <TextLink to={current.service.to} tone="light">
                    Mientras tanto, conocé {current.service.label}
                  </TextLink>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
